import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUpdateProductionDeclarationCorrectionRequestComment } from '@/services/apis/mutations/production-declaration/use-update-production-declaration-correction-request-comment';
import type { CorrectionRequest } from '@/types/core/purchase-order/correction-request';

type UseProductionDeclarationCorrectionRequestCommentEditParams = {
  commentsQueryKey: QueryKey;
  commodityOriginId: string;
  materialId: string;
  productionDeclarationId: string;
};

type ApiErrorResponse = {
  detail?: string;
  errors?: {
    comment?: string[];
  };
  message?: string;
};

export function useProductionDeclarationCorrectionRequestCommentEdit({
  commentsQueryKey,
  commodityOriginId,
  materialId,
  productionDeclarationId,
}: UseProductionDeclarationCorrectionRequestCommentEditParams) {
  const [editedComment, setEditedComment] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] =
    useState<CorrectionRequest | null>(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const {
    isPending: isUpdatingComment,
    mutate: updateCorrectionRequestComment,
  } = useUpdateProductionDeclarationCorrectionRequestComment();
  const handleEditClick = (comment: CorrectionRequest) => {
    setSelectedComment(comment);
    setEditedComment(comment.comment);
    setIsEditDialogOpen(true);
  };

  const handleEditCancel = () => {
    if (isUpdatingComment) {
      return;
    }
    setEditedComment('');
    setSelectedComment(null);
    setIsEditDialogOpen(false);
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    if (isUpdatingComment) {
      return;
    }
    if (!open) {
      handleEditCancel();
      return;
    }
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    if (selectedComment === null) {
      return;
    }

    updateCorrectionRequestComment(
      {
        productionDeclarationId,
        materialId,
        commodityOriginId,
        correctionRequestId: selectedComment.id,
        comment: editedComment,
      },
      {
        onSuccess: (updatedComment) => {
          queryClient.setQueryData<CorrectionRequest[]>(
            commentsQueryKey,
            (currentComments = []) =>
              currentComments.map((currentComment) =>
                currentComment.id === updatedComment.id
                  ? updatedComment
                  : currentComment,
              ),
          );
          setEditedComment('');
          setSelectedComment(null);
          setIsEditDialogOpen(false);
          toast.success(t('materialCommodityOrigins.comments.updateSuccess'));
        },
        onError: (error) => {
          const description = axios.isAxiosError<ApiErrorResponse>(error)
            ? (error.response?.data?.message ??
              error.response?.data?.detail ??
              error.response?.data?.errors?.comment?.at(0) ??
              t('materialCommodityOrigins.comments.updateError'))
            : t('materialCommodityOrigins.comments.updateError');

          toast.error(tCommon('error'), {
            description,
          });
        },
      },
    );
  };

  return {
    editedComment,
    handleEditCancel,
    handleEditClick,
    handleEditDialogOpenChange,
    handleEditSubmit,
    isEditDialogOpen,
    isUpdatingComment,
    setEditedComment,
  };
}
